import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ApiSystemSettingsService } from '../../../../core/services/api/system-settings/api-system-settings.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import {
  LiffHomeMenuCategory,
  LiffHomeMenuConfig,
  LiffHomeMenuConfigResponseDto,
  LiffHomeMenuMoreItem,
  LiffHomeMenuQuickItem,
  LIFF_HOME_GLOBAL_CONFIG_ENTITY_ID,
  LIFF_HOME_QUICK_MAX_VISIBLE,
  LIFF_HOME_VISIBILITY_OPTIONS,
  createEmptyLiffHomeMenuItem,
} from '../../../../core/models/system-settings/liff-home-menu.model';
import { ModalAdminActivityHistoryComponent } from '../../modals/system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-liff-home-menu',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalAdminActivityHistoryComponent],
  templateUrl: './liff-home-menu.component.html',
  styleUrl: './liff-home-menu.component.css',
})
export class LiffHomeMenuComponent implements OnInit {
  private readonly api = inject(ApiSystemSettingsService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly cookieService = inject(CookieService);

  readonly visibilityOptions = LIFF_HOME_VISIBILITY_OPTIONS;
  readonly activityEntityTypes = AdminActivityEntityTypes;
  readonly globalEntityId = LIFF_HOME_GLOBAL_CONFIG_ENTITY_ID;
  readonly quickMaxVisible = LIFF_HOME_QUICK_MAX_VISIBLE;

  activeTab = signal<'catalog' | 'quick' | 'more'>('quick');
  config = signal<LiffHomeMenuConfig | null>(null);
  configRowId = signal(0);
  isDefaultConfig = signal(true);
  isLoading = signal(false);
  isSaving = signal(false);
  isImportModalOpen = signal(false);
  selectedFileName = signal('');
  pendingImportConfig = signal<LiffHomeMenuConfig | null>(null);
  importSummary = signal({ catalogCount: 0, quickCount: 0, moreCategories: 0, moreItems: 0 });
  showActivityHistory = false;
  isCatalogFormOpen = signal(false);
  catalogFormMode = signal<'create' | 'edit'>('create');
  catalogForm = signal<LiffHomeMenuMoreItem>(createEmptyLiffHomeMenuItem());
  catalogFormOriginalKey = '';

  /** Catalog key selected for Add to Quick */
  addQuickKey = '';
  /** Catalog key selected for Add to More (per category via map) */
  addMoreKeyByCategory: Record<string, string> = {};

  sortedCatalog = computed(() => {
    const cfg = this.config();
    if (!cfg?.catalog) return [];
    return [...cfg.catalog].sort((a, b) => a.sortOrder - b.sortOrder || a.key.localeCompare(b.key));
  });

  sortedQuick = computed(() => {
    const cfg = this.config();
    if (!cfg?.quick) return [];
    return [...cfg.quick].sort((a, b) => a.sortOrder - b.sortOrder);
  });

  sortedMoreCategories = computed(() => {
    const cfg = this.config();
    if (!cfg?.more) return [];
    return [...cfg.more]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((cat) => ({
        ...cat,
        items: [...(cat.items ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
      }));
  });

  /** Catalog items not yet in Quick (exclude more-pin for picker convenience) */
  catalogAvailableForQuick = computed(() => {
    const cfg = this.config();
    if (!cfg) return [];
    const used = new Set((cfg.quick ?? []).map((x) => x.key));
    return this.sortedCatalog().filter((c) => !used.has(c.key));
  });

  catalogAvailableForMoreCategory = computed(() => {
    const cfg = this.config();
    const result: Record<string, LiffHomeMenuMoreItem[]> = {};
    if (!cfg) return result;
    for (const cat of cfg.more ?? []) {
      const used = new Set((cat.items ?? []).map((x) => x.key));
      result[cat.categoryKey] = this.sortedCatalog().filter((c) => !used.has(c.key) && !this.isMorePin(c));
    }
    return result;
  });

  quickVisibleCount = computed(() =>
    this.sortedQuick().filter((x) => x.visibility !== 'hidden').length
  );

  ngOnInit(): void {
    this.loadConfig();
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get canShowActivityHistory(): boolean {
    return this.configRowId() > 0;
  }

  setTab(tab: 'catalog' | 'quick' | 'more'): void {
    this.activeTab.set(tab);
  }

  loadConfig(): void {
    this.isLoading.set(true);
    this.api.getLiffHomeMenuConfig().subscribe({
      next: (res) => {
        const mapped = this.mapResponse(res);
        this.config.set(mapped.config);
        this.configRowId.set(mapped.id);
        this.isDefaultConfig.set(mapped.isDefault);
        this.addQuickKey = '';
        this.addMoreKeyByCategory = {};
        this.isLoading.set(false);
      },
      error: () => {
        this.toast.error('โหลดเมนู LIFF Home ล้มเหลว');
        this.isLoading.set(false);
      },
    });
  }

  onSave(): void {
    const cfg = this.config();
    if (!cfg || !this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์บันทึก');
      return;
    }

    const payload = this.buildPayload(cfg);
    if (!payload) return;

    this.isSaving.set(true);
    this.api.saveLiffHomeMenuConfig(payload).subscribe({
      next: (res) => {
        if (res?.isSuccess ?? res?.IsSuccess) {
          this.toast.success(res?.message ?? res?.Message ?? 'บันทึกสำเร็จ');
          this.loadConfig();
        } else {
          this.toast.error(res?.message ?? res?.Message ?? 'บันทึกไม่สำเร็จ');
        }
        this.isSaving.set(false);
      },
      error: () => {
        this.toast.error('เกิดข้อผิดพลาดขณะบันทึก');
        this.isSaving.set(false);
      },
    });
  }

  exportJson(): void {
    const cfg = this.config();
    if (!cfg) {
      this.toast.error('ไม่มีข้อมูลส่งออก');
      return;
    }
    const blob = new Blob([JSON.stringify(cfg, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `liff-home-menu-config-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    this.selectedFileName.set(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const parsed = JSON.parse(text) as LiffHomeMenuConfig;
        if (!parsed?.quick || !Array.isArray(parsed.quick)) {
          this.toast.error('JSON ต้องมี quick[]');
          return;
        }
        if (!parsed.more || !Array.isArray(parsed.more)) {
          parsed.more = [];
        }
        this.ensureCatalog(parsed);
        let moreItems = 0;
        parsed.more.forEach((c) => {
          moreItems += c.items?.length ?? 0;
        });
        this.pendingImportConfig.set(parsed);
        this.importSummary.set({
          catalogCount: parsed.catalog?.length ?? 0,
          quickCount: parsed.quick.length,
          moreCategories: parsed.more.length,
          moreItems,
        });
        this.isImportModalOpen.set(true);
      } catch {
        this.toast.error('ไฟล์ JSON ไม่ถูกต้อง');
      } finally {
        input.value = '';
      }
    };
    reader.readAsText(file);
  }

  closeImportModal(): void {
    this.isImportModalOpen.set(false);
    this.pendingImportConfig.set(null);
    this.selectedFileName.set('');
  }

  confirmImport(): void {
    const imported = this.pendingImportConfig();
    if (!imported || !this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์นำเข้า');
      return;
    }

    this.ensureCatalog(imported);
    const payload = this.buildPayload(imported);
    if (!payload) return;

    this.isSaving.set(true);
    this.api.importLiffHomeMenuConfig(payload).subscribe({
      next: (res) => {
        if (res?.isSuccess ?? res?.IsSuccess) {
          this.toast.success(res?.message ?? res?.Message ?? 'นำเข้าสำเร็จ');
          this.closeImportModal();
          this.loadConfig();
        } else {
          this.toast.error(res?.message ?? res?.Message ?? 'นำเข้าไม่สำเร็จ');
        }
        this.isSaving.set(false);
      },
      error: () => {
        this.toast.error('เกิดข้อผิดพลาดขณะนำเข้า');
        this.isSaving.set(false);
      },
    });
  }

  addToQuick(): void {
    const cfg = this.config();
    if (!cfg || !this.canUpdate || !this.addQuickKey) return;

    const source = (cfg.catalog ?? []).find((x) => x.key === this.addQuickKey);
    if (!source) {
      this.toast.error('ไม่พบเมนูในคลัง');
      return;
    }
    if (cfg.quick.some((x) => x.key === source.key)) {
      this.toast.error('มีใน Quick แล้ว');
      return;
    }

    const visible = cfg.quick.filter((x) => x.visibility !== 'hidden').length;
    if (visible >= this.quickMaxVisible) {
      this.toast.error(`Quick แสดงได้สูงสุด ${this.quickMaxVisible} รายการ (รวมปุ่มเพิ่มเติม)`);
      return;
    }

    const clone = this.cloneAsQuick(source);
    const pin = cfg.quick.filter((x) => this.isMorePin(x));
    const others = cfg.quick.filter((x) => !this.isMorePin(x));
    others.push(clone);
    this.reassignSort(others);
    this.reassignSort(pin);
    pin.forEach((p, i) => {
      p.sortOrder = others.length + i + 1;
    });
    cfg.quick = [...others, ...pin];
    this.config.set({ ...cfg, quick: [...cfg.quick] });
    this.addQuickKey = '';
  }

  removeFromQuick(item: LiffHomeMenuQuickItem): void {
    const cfg = this.config();
    if (!cfg || !this.canUpdate) return;
    if (this.isMorePin(item)) {
      this.toast.error('ลบปุ่ม «เพิ่มเติม» ไม่ได้');
      return;
    }
    const ordered = cfg.quick
      .filter((x) => x.key !== item.key)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    this.reassignSort(ordered);
    cfg.quick = ordered;
    this.config.set({ ...cfg, quick: [...ordered] });
  }

  addToMore(category: LiffHomeMenuCategory): void {
    const cfg = this.config();
    if (!cfg || !this.canUpdate) return;
    const key = this.addMoreKeyByCategory[category.categoryKey];
    if (!key) return;

    const source = (cfg.catalog ?? []).find((x) => x.key === key);
    if (!source) {
      this.toast.error('ไม่พบเมนูในคลัง');
      return;
    }

    const cat = cfg.more.find((c) => c.categoryKey === category.categoryKey);
    if (!cat) return;
    if (!cat.items) cat.items = [];
    if (cat.items.some((x) => x.key === source.key)) {
      this.toast.error('มีในหมวดนี้แล้ว');
      return;
    }

    const clone = this.cloneAsMore(source);
    const ordered = [...cat.items, clone].sort((a, b) => a.sortOrder - b.sortOrder);
    this.reassignSort(ordered);
    cat.items = ordered;
    this.config.set({ ...cfg, more: [...cfg.more] });
    this.addMoreKeyByCategory[category.categoryKey] = '';
  }

  removeFromMore(category: LiffHomeMenuCategory, item: LiffHomeMenuMoreItem): void {
    const cfg = this.config();
    if (!cfg || !this.canUpdate) return;
    const cat = cfg.more.find((c) => c.categoryKey === category.categoryKey);
    if (!cat?.items) return;
    const ordered = cat.items
      .filter((x) => x.key !== item.key)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    this.reassignSort(ordered);
    cat.items = ordered;
    this.config.set({ ...cfg, more: [...cfg.more] });
  }

  moveToMoreCategory(fromCat: LiffHomeMenuCategory, item: LiffHomeMenuMoreItem, toCategoryKey: string): void {
    const cfg = this.config();
    if (!cfg || !this.canUpdate || !toCategoryKey || toCategoryKey === fromCat.categoryKey) return;

    const sourceCat = cfg.more.find((c) => c.categoryKey === fromCat.categoryKey);
    const targetCat = cfg.more.find((c) => c.categoryKey === toCategoryKey);
    if (!sourceCat?.items || !targetCat) return;
    if ((targetCat.items ?? []).some((x) => x.key === item.key)) {
      this.toast.error('มีในหมวดปลายทางแล้ว');
      return;
    }

    sourceCat.items = sourceCat.items.filter((x) => x.key !== item.key);
    this.reassignSort(sourceCat.items);
    if (!targetCat.items) targetCat.items = [];
    targetCat.items = [...targetCat.items, { ...item }];
    this.reassignSort(targetCat.items);
    this.config.set({ ...cfg, more: [...cfg.more] });
  }

  moveQuick(item: LiffHomeMenuQuickItem, direction: -1 | 1): void {
    const cfg = this.config();
    if (!cfg) return;
    const list = [...cfg.quick].sort((a, b) => a.sortOrder - b.sortOrder);
    const idx = list.findIndex((x) => x.key === item.key);
    const target = idx + direction;
    if (idx < 0 || target < 0 || target >= list.length) return;
    if (this.isMorePin(list[idx]) || this.isMorePin(list[target])) return;
    [list[idx], list[target]] = [list[target], list[idx]];
    this.reassignSort(list);
    cfg.quick = list;
    this.config.set({ ...cfg, quick: [...list] });
  }

  moveMoreItem(category: LiffHomeMenuCategory, item: LiffHomeMenuMoreItem, direction: -1 | 1): void {
    const cfg = this.config();
    if (!cfg) return;
    const cat = cfg.more.find((c) => c.categoryKey === category.categoryKey);
    if (!cat?.items) return;
    const list = [...cat.items].sort((a, b) => a.sortOrder - b.sortOrder);
    const idx = list.findIndex((x) => x.key === item.key);
    const target = idx + direction;
    if (idx < 0 || target < 0 || target >= list.length) return;
    [list[idx], list[target]] = [list[target], list[idx]];
    this.reassignSort(list);
    cat.items = list;
    this.config.set({ ...cfg, more: [...cfg.more] });
  }

  onQuickVisibilityChange(item: LiffHomeMenuQuickItem, visibility: string): void {
    item.visibility = visibility as LiffHomeMenuQuickItem['visibility'];
    this.config.set({ ...(this.config() as LiffHomeMenuConfig) });
  }

  onMoreVisibilityChange(item: LiffHomeMenuMoreItem, visibility: string): void {
    item.visibility = visibility as LiffHomeMenuMoreItem['visibility'];
    this.config.set({ ...(this.config() as LiffHomeMenuConfig) });
  }

  openCreateCatalogItem(): void {
    if (!this.canUpdate) return;
    this.catalogFormMode.set('create');
    this.catalogFormOriginalKey = '';
    this.catalogForm.set(createEmptyLiffHomeMenuItem());
    this.isCatalogFormOpen.set(true);
  }

  openEditCatalogItem(item: LiffHomeMenuMoreItem): void {
    if (!this.canUpdate) return;
    this.catalogFormMode.set('edit');
    this.catalogFormOriginalKey = item.key;
    this.catalogForm.set({
      ...createEmptyLiffHomeMenuItem(),
      ...item,
      nameEN: item.nameEN || '',
      image: item.image || '',
      imageSlide: item.imageSlide || item.image || '',
      imageActive: item.imageActive || item.image || '',
      alt: item.alt || '',
      alert: item.alert || '',
      action: item.action || '',
      disableTextTH: item.disableTextTH || 'ปิดปรับปรุง',
      disableTextEN: item.disableTextEN || 'Coming soon',
    });
    this.isCatalogFormOpen.set(true);
  }

  closeCatalogForm(): void {
    this.isCatalogFormOpen.set(false);
    this.catalogFormOriginalKey = '';
  }

  patchCatalogForm(partial: Partial<LiffHomeMenuMoreItem>): void {
    this.catalogForm.set({ ...this.catalogForm(), ...partial });
  }

  saveCatalogForm(): void {
    const cfg = this.config();
    if (!cfg || !this.canUpdate) return;

    const raw = this.catalogForm();
    const normalized = this.normalizeCatalogItem(raw);
    const err = this.validateCatalogItem(normalized);
    if (err) {
      this.toast.error(err);
      return;
    }

    if (!cfg.catalog) cfg.catalog = [];

    if (this.catalogFormMode() === 'create') {
      if (cfg.catalog.some((x) => x.key === normalized.key)) {
        this.toast.error(`key '${normalized.key}' มีในคลังแล้ว`);
        return;
      }
      normalized.sortOrder = cfg.catalog.length + 1;
      cfg.catalog = [...cfg.catalog, normalized];
    } else {
      const oldKey = this.catalogFormOriginalKey;
      if (
        normalized.key !== oldKey &&
        cfg.catalog.some((x) => x.key === normalized.key)
      ) {
        this.toast.error(`key '${normalized.key}' มีในคลังแล้ว`);
        return;
      }
      cfg.catalog = cfg.catalog.map((x) =>
        x.key === oldKey ? { ...normalized, sortOrder: x.sortOrder } : x
      );
      this.syncPlacementsFromCatalog(cfg, oldKey, normalized);
    }

    this.config.set({ ...cfg, catalog: [...(cfg.catalog ?? [])], quick: [...cfg.quick], more: [...cfg.more] });
    this.closeCatalogForm();
    this.toast.success(this.catalogFormMode() === 'create' ? 'เพิ่มเมนูในคลังแล้ว — กดบันทึกเพื่อใช้บน LIFF' : 'อัปเดตเมนูแล้ว — กดบันทึกเพื่อใช้บน LIFF');
  }

  deleteCatalogItem(item: LiffHomeMenuMoreItem): void {
    const cfg = this.config();
    if (!cfg || !this.canUpdate) return;
    if (this.isMorePin(item)) {
      this.toast.error('ลบปุ่ม «เพิ่มเติม» จากคลังไม่ได้');
      return;
    }
    if (!confirm(`ลบ "${item.nameTH}" ออกจากคลัง และเอาออกจาก Quick/More ที่วางไว้?`)) {
      return;
    }
    cfg.catalog = (cfg.catalog ?? []).filter((x) => x.key !== item.key);
    const quickOrdered = cfg.quick
      .filter((x) => x.key !== item.key)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    this.reassignSort(quickOrdered);
    cfg.quick = quickOrdered;
    for (const cat of cfg.more ?? []) {
      const items = (cat.items ?? [])
        .filter((x) => x.key !== item.key)
        .sort((a, b) => a.sortOrder - b.sortOrder);
      this.reassignSort(items);
      cat.items = items;
    }
    this.reassignSort([...(cfg.catalog ?? [])].sort((a, b) => a.sortOrder - b.sortOrder));
    cfg.catalog = [...(cfg.catalog ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
    this.reassignSort(cfg.catalog);
    this.config.set({ ...cfg, catalog: [...(cfg.catalog ?? [])], quick: [...cfg.quick], more: [...cfg.more] });
  }

  private normalizeCatalogItem(raw: LiffHomeMenuMoreItem): LiffHomeMenuMoreItem {
    const image = (raw.image || '').trim();
    return {
      key: (raw.key || '').trim(),
      route: (raw.route || '').trim().replace(/^\//, ''),
      nameTH: (raw.nameTH || '').trim(),
      nameEN: (raw.nameEN || '').trim(),
      image,
      imageSlide: (raw.imageSlide || '').trim() || image,
      imageActive: (raw.imageActive || '').trim() || image,
      alt: (raw.alt || '').trim(),
      alert: (raw.alert || '').trim(),
      action: (raw.action || '').trim(),
      visibility: raw.visibility || 'enabled',
      disableTextTH: (raw.disableTextTH || '').trim(),
      disableTextEN: (raw.disableTextEN || '').trim(),
      sortOrder: raw.sortOrder || 0,
    };
  }

  private validateCatalogItem(item: LiffHomeMenuMoreItem): string | null {
    if (!item.key) return 'key ต้องไม่ว่าง';
    if (!/^[a-z0-9_]+$/i.test(item.key)) return 'key ใช้ได้เฉพาะ a-z, 0-9, _';
    if (!item.route) return 'route ต้องไม่ว่าง (path ของ LIFF เช่น vehicles-option)';
    if (!item.nameTH) return 'nameTH ต้องไม่ว่าง';
    if (!item.nameEN) return 'nameEN ต้องไม่ว่าง';
    if (!item.image) return 'image ต้องไม่ว่าง (เช่น /icons/xxx.png)';
    if (!item.alt) return 'alt ต้องไม่ว่าง';
    if (!item.disableTextTH) return 'disableTextTH ต้องไม่ว่าง';
    if (!item.disableTextEN) return 'disableTextEN ต้องไม่ว่าง';
    return null;
  }

  private syncPlacementsFromCatalog(
    cfg: LiffHomeMenuConfig,
    oldKey: string,
    catalogItem: LiffHomeMenuMoreItem
  ): void {
    cfg.quick = cfg.quick.map((q) => {
      if (q.key !== oldKey) return q;
      return {
        ...q,
        key: catalogItem.key,
        route: catalogItem.route,
        nameTH: catalogItem.nameTH,
        nameEN: catalogItem.nameEN,
        image: catalogItem.image,
        alt: catalogItem.alt,
        alert: catalogItem.alert,
        action: catalogItem.action,
        disableTextTH: catalogItem.disableTextTH,
        disableTextEN: catalogItem.disableTextEN,
      };
    });
    for (const cat of cfg.more ?? []) {
      cat.items = (cat.items ?? []).map((m) => {
        if (m.key !== oldKey) return m;
        return {
          ...m,
          key: catalogItem.key,
          route: catalogItem.route,
          nameTH: catalogItem.nameTH,
          nameEN: catalogItem.nameEN,
          image: catalogItem.image,
          imageSlide: catalogItem.imageSlide,
          imageActive: catalogItem.imageActive,
          alt: catalogItem.alt,
          alert: catalogItem.alert,
          action: catalogItem.action,
          disableTextTH: catalogItem.disableTextTH,
          disableTextEN: catalogItem.disableTextEN,
        };
      });
    }
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }

  isMorePin(item: { key: string; action?: string }): boolean {
    return item.key === 'more' || item.action === 'more';
  }

  placementLabel(key: string): string {
    const cfg = this.config();
    if (!cfg) return '';
    const parts: string[] = [];
    if (cfg.quick?.some((x) => x.key === key)) parts.push('Quick');
    for (const cat of cfg.more ?? []) {
      if (cat.items?.some((x) => x.key === key)) {
        parts.push(cat.categoryTH || cat.categoryKey);
      }
    }
    return parts.length ? parts.join(' · ') : 'ยังไม่ได้วาง';
  }

  trackQuick(_: number, item: LiffHomeMenuQuickItem): string {
    return item.key;
  }

  trackCategory(_: number, cat: LiffHomeMenuCategory): string {
    return cat.categoryKey;
  }

  trackMoreItem(_: number, item: LiffHomeMenuMoreItem): string {
    return item.key;
  }

  trackCatalog(_: number, item: LiffHomeMenuMoreItem): string {
    return item.key;
  }

  private cloneAsQuick(source: LiffHomeMenuMoreItem): LiffHomeMenuQuickItem {
    return {
      key: source.key,
      route: source.route,
      nameTH: source.nameTH,
      nameEN: source.nameEN || '',
      image: source.image || '',
      alt: source.alt || '',
      alert: source.alert || '',
      action: source.action || '',
      visibility: 'enabled',
      disableTextTH: source.disableTextTH || 'ปิดปรับปรุง',
      disableTextEN: source.disableTextEN || 'Coming soon',
      sortOrder: 999,
    };
  }

  private cloneAsMore(source: LiffHomeMenuMoreItem): LiffHomeMenuMoreItem {
    const image = source.image || '';
    return {
      ...this.cloneAsQuick(source),
      imageSlide: source.imageSlide || image,
      imageActive: source.imageActive || image,
    };
  }

  private ensureCatalog(config: LiffHomeMenuConfig): void {
    if (config.catalog?.length) return;
    const map = new Map<string, LiffHomeMenuMoreItem>();
    for (const q of config.quick ?? []) {
      if (!map.has(q.key)) {
        const image = q.image || '';
        map.set(q.key, {
          ...q,
          imageSlide: image,
          imageActive: image,
        });
      }
    }
    for (const cat of config.more ?? []) {
      for (const it of cat.items ?? []) {
        if (!map.has(it.key)) {
          map.set(it.key, this.normalizeCatalogItem(it));
        }
      }
    }
    config.catalog = [...map.values()].map((it, i) => ({ ...it, sortOrder: i + 1 }));
  }

  private reassignSort<T extends { sortOrder: number }>(items: T[]): void {
    items.forEach((item, index) => {
      item.sortOrder = index + 1;
    });
  }

  private buildPayload(config: LiffHomeMenuConfig) {
    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return null;
    }
    const clone = structuredClone(config);
    this.ensureCatalog(clone);
    return { userId, menuId, config: clone };
  }

  private resolveUserId(): number {
    const fromLocal = localStorage.getItem('userId');
    if (fromLocal) return Number(fromLocal);
    return Number(this.cookieService.get('user_id')) || 0;
  }

  private mapResponse(dto: LiffHomeMenuConfigResponseDto) {
    const config = (dto.config ?? dto.Config) as LiffHomeMenuConfig;
    const mapped: LiffHomeMenuConfig = {
      version: config?.version ?? 1,
      description: config?.description,
      catalog: config?.catalog ?? [],
      quick: config?.quick ?? [],
      more: config?.more ?? [],
    };
    this.ensureCatalog(mapped);
    const moreKeys: Record<string, string> = {};
    for (const cat of mapped.more) {
      moreKeys[cat.categoryKey] = '';
    }
    this.addMoreKeyByCategory = moreKeys;
    return {
      id: dto.id ?? dto.Id ?? 0,
      isDefault: dto.isDefault ?? dto.IsDefault ?? true,
      config: mapped,
    };
  }
}
